// Room management utilities for Chipless
import { database } from '../firebase/firebaseConfig';
import { ref, set, get, update, onValue, off, remove, onDisconnect, serverTimestamp, runTransaction } from 'firebase/database';
import { dollarsToCents, formatCurrency, formatRoundAction, getDefaultBlinds, getDefaultBuyInCents, isCurrencyLocked, resolveCurrency, isValidBetAmount, MAX_BET_CENTS } from './currency';
import { ensureAuth } from './auth';
import { 
  isBettingRoundComplete, 
  isNoBettingActionPossible,
  advanceStage, 
  getFirstToAct,
  getFirstToActPostFlop, 
  calculateSidePotsFromState,
  awardPotsToWinners,
  calculatePlayerPositions,
  returnUncalledBet,
  getMaximumRaise,
  getMinimumRaise,
} from './pokerLogic';
import { trackPlayerJoin, trackHandComplete } from './analytics';
import { playerNamesFrom } from './shouldCountStats';
import { rejoinUrlForSeat } from './inviteShare';
import { ensureServerClock, serverNow } from './serverClock';
import { DEFAULT_TABLE_VIBE, DEFAULT_CALLOUT_MODE } from './winMessages';
import { blindsGoUpMinutes, blindsUpStartedAt, planBlindsGoUpOnDeal, planBlindsGoUpSetting } from './blindsGoUp';
import { applyAwardedHand } from './streetAction';
import {
  applyCashOut,
  applyImBack,
  applyJoin,
  applyReclaim,
  applySitOut,
  finalizeSitOutFlags,
  findOwnSeat,
  findPlayerByName,
  findSeatByRejoinToken,
  hostActionAllowed,
  isSeated,
  mergeLiveAwayFlags,
  mintRejoinToken,
  nextConnectedHostId,
  planHostReturn,
  occupancyCount,
  planAutoHostHandoff,
  planIssueRejoinToken,
  planTransferHost,
  tableDefaultBuyIn,
  voidUnfinishedHand,
} from './playerLedger';

async function requireHostCaller(roomData, claimedHostId) {
  const uid = await ensureAuth();
  if (!hostActionAllowed(roomData?.hostId, uid, claimedHostId, roomData?.players)) {
    throw new Error('Only the host can do that');
  }
  return uid;
}

async function hostCallerOrDeny(roomData, claimedHostId, reason = 'Only the host can do that.') {
  const uid = await ensureAuth();
  if (!hostActionAllowed(roomData?.hostId, uid, claimedHostId, roomData?.players)) {
    return { ok: false, reason, uid };
  }
  return { ok: true, uid };
}

/**
 * Calculate stage progress for visual dots
 * @param {string} stage - Current game stage
 * @returns {number} Progress from 0-5
 */
function getStageProgress(stage) {
  switch(stage) {
    case 'PRE_FLOP': return 0;
    case 'FLOP': return 3;
    case 'TURN': return 4; 
    case 'RIVER': return 5;
    case 'SHOWDOWN': return 5;
    default: return 0;
  }
}

/**
 * Get next stage name for display
 * @param {string} currentStage - Current stage
 * @returns {string} Next stage name
 */
function getNextStageName(currentStage) {
  const stages = {
    'PRE_FLOP': 'Flop',
    'FLOP': 'Turn',
    'TURN': 'River',
    'RIVER': 'Showdown'
  };
  return stages[currentStage] || 'Next Stage';
}

/**
 * Generate a unique room code (5 characters, alphanumeric).
 * Excludes visually-confusable characters: I, L, O, 0, 1.
 * 31-char alphabet → ~28M combinations.
 */
export function generateRoomCode() {
  const chars = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
  let result = '';
  for (let i = 0; i < 5; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}

/**
 * Update room's last activity timestamp
 * @param {string} roomCode - Room code to update
 */
async function updateRoomActivity(roomCode) {
  try {
    await set(ref(database, `rooms/${roomCode}/lastActivity`), Date.now());
  } catch (error) {
    console.error('Failed to update room activity:', error);
  }
}

/**
 * Verify user is host of the room
 * @param {string} roomCode - Room code to check
 * @param {string} userId - User ID to verify
 * @returns {Promise<boolean>} True if user is host
 */
export async function verifyHost(roomCode, userId) {
  try {
    const roomRef = ref(database, `rooms/${roomCode}`);
    const snapshot = await get(roomRef);
    
    if (!snapshot.exists()) {
      return false;
    }
    
    const roomData = snapshot.val();
    return hostActionAllowed(roomData.hostId, userId, userId, roomData.players);
  } catch (error) {
    console.error('Failed to verify host:', error);
    return false;
  }
}

/**
 * Verify user is member of the room
 * @param {string} roomCode - Room code to check
 * @param {string} userId - User ID to verify
 * @returns {Promise<boolean>} True if user is member
 */
export async function verifyMember(roomCode, userId) {
  try {
    const roomRef = ref(database, `rooms/${roomCode}`);
    const snapshot = await get(roomRef);
    
    if (!snapshot.exists()) {
      return false;
    }
    
    const roomData = snapshot.val();
    return roomData.hostId === userId || (roomData.players && roomData.players[userId]);
  } catch (error) {
    console.error('Failed to verify membership:', error);
    return false;
  }
}

/**
 * Create a new game room with default settings
 * @param {string} hostName - Name of the host player
 * @param {number} chipAmount - Starting chip amount for host (in dollars)
 * @returns {Promise<Object>} Room data with code and initial state
 */
export async function createRoom(hostName, chipAmount = 50, currency) {
  const roomCode = generateRoomCode();
  // Await anon auth inline rather than trusting the App.jsx warm-up — that
  // warm-up is fire-and-forget and a fast-tapping user can race it.
  const hostId = await ensureAuth();
  const currencyCode = resolveCurrency(currency);
  const blinds = getDefaultBlinds(currencyCode);
  
  // Default game settings from Chipless.md
  const initialGameState = {
    roomCode,
    hostId,
    createdAt: Date.now(),
    lastActivity: Date.now(), // Track room activity
    status: 'LOBBY', // LOBBY, PLAYING, ENDED
    
    // Game settings
    settings: {
      smallBlind: blinds.smallBlind,
      bigBlind: blinds.bigBlind,
      maxPlayers: 10,
      enableRebuys: true,
      tableVibe: DEFAULT_TABLE_VIBE,     // Win-message personality ('classic' | 'hype' | 'trashTalk')
      callouts: DEFAULT_CALLOUT_MODE,    // Winner callouts ('off' | 'friendly' | 'adult')
      currency: currencyCode,            // ISO 4217; missing on old rooms → USD at read
      awayPlayersPayBlinds: false,       // Host toggle; applies next hand. Default skip + owe.
      blindsGoUpMinutes: 0,              // 0 Off, or 1–180 minutes. Timer starts on first deal.
    },
    
    // Players object - host is first player
    players: {
      [hostId]: {
        id: hostId,
        name: hostName,
        isHost: true,
        isApproved: true,
        stack: dollarsToCents(chipAmount.toString()), // Host's chosen chip amount
        totalInvestment: dollarsToCents(chipAmount.toString()), // Track total money invested
        role: 'PLAYER',
        status: 'active',
        joinedAt: Date.now()
      }
    },
    
    // Game state (will be populated when game starts)
    gameState: {
      stage: 'PRE_FLOP',
      dealerId: null,
      currentTurnPlayerId: null,
      handNumber: 0,
      pots: [],
      canAdvanceStage: false,
      nextStage: null,
      nextStageName: null,
      stageProgress: 0,
      roundActions: []
    }
  };
  
  try {
    // Write room to Firebase
    const roomRef = ref(database, `rooms/${roomCode}`);
    await set(roomRef, initialGameState);
    
    console.log(`🎰 Created room ${roomCode} with host ${hostName}`);
    
    return {
      roomCode,
      hostId,
      gameState: initialGameState
    };
  } catch (error) {
    console.error('Failed to create room:', error);
    throw error;
  }
}

/**
 * Read a room without joining. Used by the join modal for the in-progress hint.
 * @param {string} roomCode
 * @returns {Promise<Object|null>}
 */
export async function getRoom(roomCode) {
  if (!roomCode) return null;
  const snapshot = await get(ref(database, `rooms/${roomCode}`));
  if (!snapshot.exists()) return null;
  return snapshot.val();
}

/**
 * Join an existing room — new player, same-device reconnect, or name reclaim
 * of a vacated/disconnected seat. Reclaim never writes a second buy-in.
 * New players copy the host's buy-in (`tableDefaultBuyIn`) unless they pass
 * `{ buyInCents }` (min $1.00, same validation as setLobbyBuyIn). A bare
 * number as the third argument is a legacy dollar amount and is ignored.
 * No host approval: a new player is seated at once (lobby list, or a
 * "Next hand" seat mid-game). Same phone (uid) reclaims its seat. A new
 * phone needs the host-issued `/r/:code/:token` link — typing a taken
 * name does not steal a disconnected seat. After a lobby removal the
 * seat is gone and they join fresh.
 *
 * @param {string} roomCode
 * @param {string} playerName
 * @param {number|{ buyInCents?: number }} [chipAmountOrOptions]
 * @returns {Promise<Object>} Room data and player info
 */
export async function joinRoom(roomCode, playerName, chipAmountOrOptions) {
  // Await anon auth inline so a fast-tapping user on a fresh page (e.g.
  // QR/deep-link first load) can't race the App.jsx warm-up.
  const playerId = await ensureAuth();
  
  try {
    const roomRef = ref(database, `rooms/${roomCode}`);
    const snapshot = await get(roomRef);
    
    if (!snapshot.exists()) {
      throw new Error('Room not found. Please check the room code.');
    }
    
    const roomData = snapshot.val();
    
    if (roomData.status === 'ENDED') {
      throw new Error('This game has already ended.');
    }

    const players = roomData.players || {};
    const now = Date.now();

    // Same anonymous UID (or a controller who already claimed this seat) —
    // reconnect / reclaim without touching buy-in or stack. A presence-only
    // node (no id) is not a seat; the fresh join below overwrites it.
    const mine = findOwnSeat(players, playerId);
    if (mine) {
      return persistReclaim(roomCode, roomData, mine.id, playerId, now);
    }

    // A taken name is taken — even a disconnected seat. New phones reclaim
    // through claimRejoinLink, not by typing the dropped player's name.
    const named = findPlayerByName(players, playerName);
    if (named) {
      throw new Error('Name already taken. Please choose a different name.');
    }

    const maxPlayers = roomData.settings?.maxPlayers || 10;
    if (occupancyCount(players) >= maxPlayers) {
      throw new Error('Room is full.');
    }

    let stack = tableDefaultBuyIn(players, roomData.hostId);
    const requested = chipAmountOrOptions && typeof chipAmountOrOptions === 'object'
      ? Number(chipAmountOrOptions.buyInCents)
      : NaN;
    if (Number.isFinite(requested)) {
      if (!isValidBetAmount(requested, 100, MAX_BET_CENTS)) {
        throw new Error('Please enter a valid amount');
      }
      stack = requested;
    }
    const joined = applyJoin(players, {
      id: playerId,
      name: playerName,
      stack,
      totalInvestment: stack,
    }, { maxPlayers, now, playing: roomData.status === 'PLAYING' });
    if (!joined.ok) {
      throw new Error(joined.reason);
    }

    // Single-path write so two people joining at once cannot clobber each other.
    await update(roomRef, {
      [`players/${playerId}`]: toStoredPlayer(joined.player),
      lastActivity: now,
    });

    // 🔥 ANALYTICS: players_joined. This used to fire on the host's Approve;
    // with no approval step the join itself is the event, and it happens on
    // exactly one device (the joiner's), so it cannot double count.
    trackPlayerJoin(true, playerNamesFrom(joined.players));

    console.log(`🎰 Player ${playerName} joined room ${roomCode}`);

    return {
      roomCode,
      playerId,
      reclaimed: false,
      gameState: { ...roomData, players: joined.players, lastActivity: now },
    };
  } catch (error) {
    console.error('Failed to join room:', error);
    throw error;
  }
}

async function persistReclaim(roomCode, roomData, seatId, controllerUid, now) {
  const result = applyReclaim(roomData.players || {}, seatId, { controllerUid, now });
  if (!result.ok) {
    throw new Error(result.reason);
  }
  const stored = toStoredPlayer(result.player);
  await update(ref(database, `rooms/${roomCode}`), {
    [`players/${seatId}`]: stored,
    lastActivity: now,
  });
  console.log(`🎰 Player ${result.player.name} reclaimed seat in room ${roomCode}`);
  return {
    roomCode,
    playerId: seatId,
    reclaimed: true,
    gameState: {
      ...roomData,
      players: result.players,
      lastActivity: now,
    },
  };
}

/**
 * Host-only. Mint a one-time `/r/:code/:token` link for a disconnected seat.
 * A new token replaces any earlier one for that seat.
 */
export async function issueRejoinLink(roomCode, playerId, hostId) {
  const roomRef = ref(database, `rooms/${roomCode}`);
  const snapshot = await get(roomRef);
  if (!snapshot.exists()) {
    throw new Error('Room not found');
  }
  const roomData = snapshot.val();
  await requireHostCaller(roomData, hostId);
  const token = mintRejoinToken();
  const plan = planIssueRejoinToken(roomData, playerId, token, Date.now());
  if (!plan.ok) {
    throw new Error(plan.reason);
  }
  await update(roomRef, plan.updates);
  return {
    token: plan.token,
    url: rejoinUrlForSeat(roomCode, plan.token),
    player: plan.player,
  };
}

/**
 * Transaction updater for claiming a rejoin token.
 * Firebase invokes this first with `null` when the client has not cached the
 * player node — returning `undefined` aborts and never retries. Return
 * `current` on null so the SDK retries with the server value.
 */
export function applyRejoinTokenTransaction(current, { token, uid, now } = {}) {
  if (current == null) return current;
  if (current.rejoinToken !== token) return undefined;
  const result = applyReclaim({ [current.id]: current }, current.id, {
    controllerUid: uid,
    now,
    viaToken: true,
  });
  if (!result.ok) return undefined;
  return toStoredPlayer(result.player);
}

/**
 * Open `/r/:code/:token`. First phone to claim the live token takes the seat.
 * A used, expired, or missing token returns `{ ok: false, roomCode }` so the
 * join form can open with the code prefilled.
 */
export async function claimRejoinLink(roomCode, token) {
  const uid = await ensureAuth();
  const code = String(roomCode || '').trim().toUpperCase().slice(0, 5);
  const tok = String(token || '').trim();
  if (!code || !tok) {
    return { ok: false, reason: 'invalid', roomCode: code };
  }

  const roomRef = ref(database, `rooms/${code}`);
  const snapshot = await get(roomRef);
  if (!snapshot.exists()) {
    return { ok: false, reason: 'not-found', roomCode: code };
  }
  const roomData = snapshot.val();
  if (roomData.status === 'ENDED') {
    return { ok: false, reason: 'ended', roomCode: code };
  }

  const seat = findSeatByRejoinToken(roomData.players || {}, tok);
  if (!seat || !isSeated(seat)) {
    return { ok: false, reason: 'invalid', roomCode: code };
  }

  const now = Date.now();
  const playerRef = ref(database, `rooms/${code}/players/${seat.id}`);
  const tx = await runTransaction(playerRef, (current) =>
    applyRejoinTokenTransaction(current, { token: tok, uid, now })
  );

  const claimed = tx.committed ? tx.snapshot?.val() : null;
  if (!tx.committed || !claimed?.id) {
    return { ok: false, reason: 'used', roomCode: code };
  }

  await update(roomRef, { lastActivity: now });
  const latest = await get(roomRef);
  const gameState = latest.exists()
    ? latest.val()
    : { ...roomData, players: { ...(roomData.players || {}), [seat.id]: tx.snapshot.val() }, lastActivity: now };

  console.log(`🎰 Player ${seat.name} reclaimed seat via rejoin link in room ${code}`);
  return {
    ok: true,
    roomCode: code,
    playerId: seat.id,
    reclaimed: true,
    gameState,
  };
}

/**
 * Register a soft-presence record. On disconnect we ONLY stamp a lastSeen
 * timestamp — we do not remove the player, so refreshes don't end the game.
 * lastSeen must be a Firebase server timestamp: Date.now() is evaluated when
 * onDisconnect is *armed* (connect time), so a 10-minute session looked like
 * a 10-minute drop and auto-handoff fired in 1–2s.
 * Returns an unsubscribe function that cancels the onDisconnect handler.
 *
 * @param {string} roomCode
 * @param {string} playerId
 * @returns {Promise<Function>} cancel function
 */
export async function registerPresence(roomCode, playerId) {
  if (!roomCode || !playerId) return () => {};
  try {
    ensureServerClock();
    const presenceRef = ref(database, `rooms/${roomCode}/players/${playerId}/presence`);
    const playerRef = ref(database, `rooms/${roomCode}/players/${playerId}`);
    const connectedRef = ref(database, '.info/connected');
    let od = null;
    let cancelled = false;
    const markOnline = async () => {
      // Re-arm onDisconnect first: the server drops it after it fires, so
      // after any socket drop the seat would stay connected:false forever.
      od = onDisconnect(presenceRef);
      await od.set({ connected: false, lastSeen: serverTimestamp() });
      if (cancelled) return;
      await set(presenceRef, { connected: true, lastSeen: serverTimestamp() });
      // Seat is back — any host-issued rejoin link for this seat dies.
      await update(playerRef, { rejoinToken: null });
    };
    await markOnline();
    // Every reconnect (lock screen, app switch, Wi-Fi blip) says "I'm back".
    let first = true;
    const unsubscribe = onValue(connectedRef, (snap) => {
      if (first) { first = false; if (snap?.val?.() === true) return; }
      if (snap?.val?.() === true && !cancelled) {
        markOnline().catch((err) => console.warn('presence reconnect failed:', err));
      }
    });
    return () => {
      cancelled = true;
      if (typeof unsubscribe === 'function') unsubscribe();
      if (od) od.cancel().catch(() => {});
    };
  } catch (error) {
    console.warn('registerPresence failed:', error);
    return () => {};
  }
}

/**
 * Original host reconnected after an auto-handoff: take the table back.
 * Runs only on that host's device; re-reads so it's safe to repeat.
 */
export async function reclaimHostOnReturn(roomCode, playerId) {
  try {
    const roomRef = ref(database, `rooms/${roomCode}`);
    const snapshot = await get(roomRef);
    if (!snapshot.exists()) return { ok: false, reason: 'Room not found' };
    ensureServerClock();
    const plan = planHostReturn(snapshot.val(), playerId, serverNow());
    if (!plan.ok) return plan;
    await update(roomRef, plan.updates);
    return { ok: true, noop: !!plan.noop };
  } catch (error) {
    console.warn('reclaimHostOnReturn failed:', error);
    return { ok: false, reason: error.message };
  }
}

/**
 * Leave a room.
 * Mid-game host Leave hands the table to the next connected seat and cashes
 * the host out — it does not End & Settle and does not delete the room.
 * Lobby host still deletes the room (the game has not started).
 * During PLAYING a non-host is cashed out in place so settlement keeps them
 * and they can reclaim the seat later. Lobby still removes the node.
 * ENDED is a local leave only, so other phones keep seeing who-owes-who.
 *
 * @param {string} roomCode
 * @param {string} playerId
 */
export async function leaveRoom(roomCode, playerId) {
  try {
    console.log(`🎰 Player ${playerId} leaving room ${roomCode}`);
    
    const roomRef = ref(database, `rooms/${roomCode}`);
    const snapshot = await get(roomRef);
    
    if (!snapshot.exists()) {
      console.log('Room no longer exists');
      return { ok: true };
    }
    
    const roomData = snapshot.val();
    
    if (roomData.hostId === playerId) {
      if (roomData.status === 'PLAYING') {
        const plan = planHostLeave(roomData, playerId);
        if (!plan.ok) {
          console.log(`🎰 Host leave aborted: ${plan.reason}`);
          return { ok: false, reason: plan.reason };
        }
        await update(roomRef, plan.updates);
        console.log(`🎰 Host left, handed the table to ${plan.nextHostId}`);
        return { ok: true, nextHostId: plan.nextHostId };
      }
      console.log(`🎰 Host leaving lobby, deleting room ${roomCode}`);
      await remove(roomRef);
      return { ok: true };
    }

    if (roomData.status === 'ENDED') {
      return { ok: true };
    }

    if (roomData.status === 'PLAYING') {
      await cashOutPlayerInRoom(roomCode, playerId, roomData);
      return { ok: true };
    }
    
    await remove(ref(database, `rooms/${roomCode}/players/${playerId}`));
    await updateRoomActivity(roomCode);
    
    console.log(`🎰 Player ${playerId} removed from room ${roomCode}`);
    return { ok: true };
  } catch (error) {
    console.error('Failed to leave room:', error);
    return { ok: false, reason: error.message };
  }
}

/**
 * Fold if needed, then freeze the remaining stack on the player record — the
 * shared path for a player leaving and the host removing them mid-game.
 * Pure: returns the multi-path update for rooms/{code}; never mutates input.
 *
 * Chips already in the pot stay there (exactly like a fold), so the hand and
 * pot math continue unchanged for everyone else. The remaining stack is
 * frozen as `cashedOut`, which is what settlement uses. Writes per-player so
 * a concurrent joiner is never wiped. Drops the undo snapshot: restoring a
 * pre-leave snapshot would put a departed player back in the hand.
 *
 * @returns {{ ok: true, updates, players, alreadyLeft?: boolean } | { ok: false, reason }}
 */
export function buildCashOutUpdates(roomData, playerId, now = Date.now()) {
  const player = roomData?.players?.[playerId];
  if (!player) return { ok: false, reason: 'Player not found' };
  if (player.status === 'left') {
    return { ok: true, alreadyLeft: true, updates: { lastActivity: now }, players: { ...roomData.players } };
  }

  const othersLive = Object.values(roomData.players || {}).filter(
    (p) => p && p.id !== playerId && p.isActive && !p.hasFolded
  );
  // Last player standing just picks up their own pot chips — folding would
  // leave a 0-player hand and stick the contributions.
  const skipFold = othersLive.length === 0 && player.isActive && !player.hasFolded;

  const folded = skipFold
    ? {
        players: Object.values(roomData.players || {}),
        gameState: roomData.gameState || {},
      }
    : foldForLeave(roomData, playerId);
  const playersMap = {};
  folded.players.forEach((p) => {
    if (p && p.id) playersMap[p.id] = p;
  });

  const cashed = applyCashOut(playersMap, playerId, now);
  if (!cashed.ok) {
    return {
      ok: false,
      reason: cashed.reason === 'all-in'
        ? 'This player is all-in. Wait until the hand ends.'
        : cashed.reason,
    };
  }

  const updates = { lastActivity: now, undoSnapshot: null };
  Object.values(cashed.players).forEach((p) => {
    updates[`players/${p.id}`] = toStoredPlayer(p);
  });
  if (folded.gameState) {
    updates.gameState = toStoredGameState(folded.gameState);
  }
  if (roomData.rebuyRequests?.[playerId]) {
    updates[`rebuyRequests/${playerId}`] = null;
  }
  return { ok: true, updates, players: cashed.players };
}

async function cashOutPlayerInRoom(roomCode, playerId, roomData) {
  const built = buildCashOutUpdates(roomData, playerId);
  if (!built.ok) throw new Error(built.reason);
  if (built.alreadyLeft) return built;
  await update(ref(database, `rooms/${roomCode}`), built.updates);
  console.log(`🎰 Player ${playerId} cashed out of room ${roomCode}`);
  return built;
}

/**
 * Fold a leaving player without stealing someone else's turn.
 * Their turn, or a fold that ends the round/hand, goes through processAction.
 * Otherwise they are simply marked folded and the current actor is unchanged.
 */
function foldForLeave(roomData, playerId) {
  const players = Object.values(roomData.players || {}).sort(
    (a, b) => (a.joinedAt || 0) - (b.joinedAt || 0)
  );
  const player = players.find((p) => p.id === playerId);
  const gameState = roomData.gameState || {};
  const settings = roomData.settings;

  if (!player || !player.isActive || player.hasFolded || player.isAllIn) {
    return { players, gameState };
  }

  const wouldBeFolded = players.map((p) =>
    p.id === playerId ? { ...p, hasFolded: true, hasActedThisRound: true } : p
  );
  const active = wouldBeFolded.filter((p) => !p.hasFolded && p.isActive);
  const roundDone =
    active.length <= 1 ||
    isBettingRoundComplete(wouldBeFolded, null, gameState.stage, settings?.bigBlind || 200);
  const theirTurn = gameState.currentTurnPlayerId === playerId;

  if (theirTurn || roundDone) {
    const gs = theirTurn ? gameState : { ...gameState, currentTurnPlayerId: playerId };
    const result = processAction(gs, players, playerId, { type: 'FOLD' }, settings);
    if (result) {
      return { players: result.updatedPlayers, gameState: result.updatedGameState };
    }
  }

  return { players: wouldBeFolded, gameState };
}

function seatCallerAllowed(player, uid) {
  if (!player || !uid) return false;
  return player.id === uid || player.controllerUid === uid;
}

/**
 * Sit out. Holding cards: pending (auto check/fold this hand). Folded or
 * between hands: Away immediately. Player's own seat only.
 */
export function planSitOut(roomData, playerId, now = Date.now()) {
  if (!roomData) return { ok: false, reason: 'Room not found' };
  if (roomData.status !== 'PLAYING') {
    return { ok: false, reason: 'Sit out is only during a game' };
  }
  const sat = applySitOut(roomData.players, playerId, {
    winnerInfo: roomData.gameState?.winnerInfo,
  });
  if (!sat.ok) return sat;
  if (sat.alreadyAway) {
    return { ok: true, alreadyAway: true, updates: { lastActivity: now } };
  }
  const updates = {
    lastActivity: now,
    [`players/${playerId}`]: toStoredPlayer(sat.player),
  };
  return { ok: true, pending: !!sat.pending, updates, players: sat.players };
}

export function planImBack(roomData, playerId, now = Date.now()) {
  if (!roomData) return { ok: false, reason: 'Room not found' };
  const back = applyImBack(roomData.players, playerId);
  if (!back.ok) return back;
  if (back.alreadyBack) {
    return { ok: true, alreadyBack: true, updates: { lastActivity: now } };
  }
  // Field writes only — a whole-node replace would delete missedSb/Bb
  // counters if this snapshot omitted them (Firebase drops null keys).
  const updates = {
    lastActivity: now,
    [`players/${playerId}/away`]: null,
    [`players/${playerId}/sitOutPending`]: null,
    [`players/${playerId}/isActive`]: back.player.isActive === true,
    [`players/${playerId}/hasFolded`]: back.player.hasFolded === true,
  };
  return {
    ok: true,
    cancelledPending: !!back.cancelledPending,
    waitingNext: !!back.waitingNext,
    updates,
    players: back.players,
  };
}

export async function sitOut(roomCode, playerId) {
  const uid = await ensureAuth();
  const roomRef = ref(database, `rooms/${roomCode}`);
  const snapshot = await get(roomRef);
  if (!snapshot.exists()) throw new Error('Room not found');
  const roomData = snapshot.val();
  const player = roomData.players?.[playerId];
  if (!seatCallerAllowed(player, uid)) {
    throw new Error('Only you can sit out of your seat');
  }
  const plan = planSitOut(roomData, playerId);
  if (!plan.ok) throw new Error(plan.reason);
  if (!plan.alreadyAway) {
    await update(roomRef, plan.updates);
  }
  return plan;
}

export async function imBack(roomCode, playerId) {
  const uid = await ensureAuth();
  const roomRef = ref(database, `rooms/${roomCode}`);
  const snapshot = await get(roomRef);
  if (!snapshot.exists()) throw new Error('Room not found');
  const roomData = snapshot.val();
  const player = roomData.players?.[playerId];
  if (!seatCallerAllowed(player, uid)) {
    throw new Error('Only you can come back to your seat');
  }
  const plan = planImBack(roomData, playerId);
  if (!plan.ok) throw new Error(plan.reason);
  if (!plan.alreadyBack) {
    await update(roomRef, plan.updates);
  }
  return plan;
}

export async function setAwayPlayersPayBlinds(roomCode, hostId, enabled) {
  const roomRef = ref(database, `rooms/${roomCode}`);
  const snapshot = await get(roomRef);
  if (!snapshot.exists()) throw new Error('Room not found');
  const roomData = snapshot.val();
  await requireHostCaller(roomData, hostId);
  // Path update so a stale whole-settings `set` cannot drop this key.
  await update(roomRef, {
    'settings/awayPlayersPayBlinds': !!enabled,
    lastActivity: Date.now(),
  });
}

export function blindsGoUpDealFromSettings(settings) {
  return planBlindsGoUpOnDeal({
    minutes: blindsGoUpMinutes(settings),
    startedAt: blindsUpStartedAt(settings),
    smallBlind: settings?.smallBlind || 100,
    bigBlind: settings?.bigBlind || 200,
    now: serverNow(),
    startedAtWrite: serverTimestamp(),
  });
}

export async function setBlindsGoUpMinutes(roomCode, hostId, minutes) {
  const roomRef = ref(database, `rooms/${roomCode}`);
  const snapshot = await get(roomRef);
  if (!snapshot.exists()) throw new Error('Room not found');
  const roomData = snapshot.val();
  await requireHostCaller(roomData, hostId);
  const { updates } = planBlindsGoUpSetting({
    minutes,
    playing: roomData.status === 'PLAYING',
    startedAtWrite: serverTimestamp(),
    previousMinutes: blindsGoUpMinutes(roomData.settings),
  });
  await update(roomRef, {
    ...updates,
    lastActivity: Date.now(),
  });
}

/**
 * Listen for room updates
 * @param {string} roomCode - Room code to monitor
 * @param {Function} callback - Callback function to handle updates
 * @returns {Function} Unsubscribe function
 */
export function subscribeToRoom(roomCode, callback) {
  const roomRef = ref(database, `rooms/${roomCode}`);
  
  const unsubscribe = onValue(roomRef, (snapshot) => {
    if (snapshot.exists()) {
      const roomData = snapshot.val();
      // Don't update activity here - it causes infinite loops
      // Activity is updated when actual actions occur, not on every read
      callback(roomData);
    } else {
      callback(null); // Room was deleted
    }
  }, (error) => {
    console.error('Room subscription error:', error);
    callback(null);
  });
  
  // Return cleanup function
  return () => off(roomRef, 'value', unsubscribe);
}

/**
 * The whole multi-path write for a host removal. Pure, so tests can check the
 * ledger without Firebase.
 *
 * Lobby: the seat is deleted (no money in play yet).
 * Playing: the same cash-out as a leave — folded out of the live hand with
 *   their pot chips left in the pot, remaining stack frozen as `cashedOut`
 *   so who-owes-who still includes them.
 * No new database paths: the removed phone notices its own seat is gone (or
 *   marked left) and returns home. Nothing blocks a rejoin.
 *
 * @returns {{ ok: true, updates, name } | { ok: false, reason }}
 */
export function planRemovePlayer(roomData, playerId, now = Date.now()) {
  if (!roomData) return { ok: false, reason: 'Room not found' };
  const target = roomData.players?.[playerId];
  if (!target) return { ok: false, reason: 'Player not found' };
  if (playerId === roomData.hostId || target.isHost) {
    return { ok: false, reason: 'The host cannot be removed' };
  }
  if (roomData.status === 'ENDED') {
    return { ok: false, reason: 'This game has already ended.' };
  }

  if (roomData.status === 'PLAYING') {
    const built = buildCashOutUpdates(roomData, playerId, now);
    if (!built.ok) return built;
    return {
      ok: true,
      name: target.name,
      updates: built.updates,
      players: built.players,
    };
  }

  return {
    ok: true,
    name: target.name,
    updates: {
      [`players/${playerId}`]: null,
      lastActivity: now,
    },
  };
}

/**
 * Remove a player from the room (host only). See planRemovePlayer.
 * @param {string} roomCode - Room code
 * @param {string} playerId - Seat to remove
 * @param {string} hostId - Host ID for verification
 */
export async function removePlayer(roomCode, playerId, hostId) {
  const roomRef = ref(database, `rooms/${roomCode}`);
  const snapshot = await get(roomRef);
  if (!snapshot.exists()) throw new Error('Room not found');

  const roomData = snapshot.val();
  await requireHostCaller(roomData, hostId);

  const plan = planRemovePlayer(roomData, playerId);
  if (!plan.ok) throw new Error(plan.reason);

  await update(roomRef, plan.updates);
  console.log(`🎰 Host removed player ${playerId} from room ${roomCode}`);
  return { ok: true, name: plan.name };
}

/**
 * Apply a host handoff. Make host and a later auto-handoff should both use
 * this so room.hostId and every `players/{id}/isHost` stay in lockstep.
 *
 * @param {string} roomCode
 * @param {string} newHostSeatId - Seat key of the player who will run the table
 * @param {string} hostId - Claimed current host (seat id or controlling uid)
 */
export async function transferHost(roomCode, newHostSeatId, hostId) {
  const roomRef = ref(database, `rooms/${roomCode}`);
  const snapshot = await get(roomRef);
  if (!snapshot.exists()) throw new Error('Room not found');

  const roomData = snapshot.val();
  await requireHostCaller(roomData, hostId);

  const plan = planTransferHost(roomData, newHostSeatId);
  if (!plan.ok) throw new Error(plan.reason);

  await update(roomRef, plan.updates);
  console.log(`🎰 Host handed the table to ${newHostSeatId} in room ${roomCode}`);
  return { ok: true, name: plan.name };
}

const autoHandoffInFlight = new Set();

/**
 * Host has been disconnected long enough: hand the table to the next
 * connected seat. Any seated client may call this; the write is skipped if
 * hostId already moved (expectedHostId) or the host is back. Uses
 * planTransferHost so hostId and every isHost flag stay in lockstep.
 */
export async function autoTransferHost(roomCode, expectedHostId) {
  const key = `${roomCode}:${expectedHostId || ''}`;
  if (autoHandoffInFlight.has(key)) {
    return { ok: false, reason: 'in-flight' };
  }
  autoHandoffInFlight.add(key);
  try {
    const roomRef = ref(database, `rooms/${roomCode}`);
    const snapshot = await get(roomRef);
    if (!snapshot.exists()) return { ok: false, reason: 'Room not found' };

    ensureServerClock();
    const plan = planAutoHostHandoff(snapshot.val(), { expectedHostId, now: serverNow() });
    if (!plan.ok) return plan;

    await update(roomRef, plan.updates);
    console.log(`🎰 Auto-handoff in ${roomCode}: ${expectedHostId} → ${plan.nextHostId}`);
    return {
      ok: true,
      name: plan.name,
      fromName: plan.fromName,
      nextHostId: plan.nextHostId,
    };
  } catch (error) {
    console.warn('autoTransferHost failed:', error);
    return { ok: false, reason: error.message };
  } finally {
    autoHandoffInFlight.delete(key);
  }
}

/**
 * Mid-game host Leave: transfer to the next connected seat, then cash the
 * leaving host out like a regular player. One write. Does not End & Settle.
 * `no-successor` means the UI should fall back to End & Settle.
 */
export function planHostLeave(roomData, playerId, now = Date.now()) {
  if (!roomData) return { ok: false, reason: 'Room not found' };
  if (roomData.status !== 'PLAYING') {
    return { ok: false, reason: 'not-playing' };
  }
  if (roomData.hostId !== playerId) {
    return { ok: false, reason: 'Not the host' };
  }
  const nextId = nextConnectedHostId(roomData.players, playerId);
  if (!nextId) return { ok: false, reason: 'no-successor' };

  const transfer = planTransferHost(roomData, nextId, now);
  if (!transfer.ok) return transfer;

  const players = {};
  Object.entries(roomData.players || {}).forEach(([id, p]) => {
    players[id] = p?.id ? { ...p, isHost: id === nextId } : p;
  });

  const cashed = buildCashOutUpdates(
    { ...roomData, hostId: nextId, players },
    playerId,
    now
  );
  if (!cashed.ok) return cashed;

  return {
    ok: true,
    nextHostId: nextId,
    nextHostName: transfer.name,
    updates: { ...cashed.updates, hostId: nextId },
  };
}

/**
 * Update room settings (host only)
 * @param {string} roomCode - Room code
 * @param {Object} newSettings - Updated settings object
 * @param {string} hostId - Host ID for verification
 */
export async function updateRoomSettings(roomCode, newSettings, hostId) {
  try {
    // Verify host permission
    const roomRef = ref(database, `rooms/${roomCode}`);
    const snapshot = await get(roomRef);
    
    if (!snapshot.exists()) {
      throw new Error('Room not found');
    }
    
    const roomData = snapshot.val();
    await requireHostCaller(roomData, hostId);

    // Currency locks at hand 1: a settings save can't change it after that.
    // Merge over live settings so a stale overlay snapshot cannot drop
    // blindsUpStartedAt (the clock writes immediately, separately from Save).
    const live = roomData.settings || {};
    const settingsToSave = {
      ...live,
      ...newSettings,
      currency: isCurrencyLocked(roomData)
        ? resolveCurrency(live.currency)
        : resolveCurrency(newSettings.currency || live.currency),
    };
    if (blindsGoUpMinutes(settingsToSave) === 0) {
      delete settingsToSave.blindsUpStartedAt;
    } else if (live.blindsUpStartedAt != null) {
      settingsToSave.blindsUpStartedAt = live.blindsUpStartedAt;
    }

    // Update settings
    await set(ref(database, `rooms/${roomCode}/settings`), settingsToSave);
    await updateRoomActivity(roomCode);
    
    console.log(`🔁 Settings updated for room ${roomCode}:`, newSettings);
  } catch (error) {
    console.error('Failed to update room settings:', error);
    throw error;
  }
}

/**
 * Pure: the update for a pre-game currency change. Blinds and stacks that are
 * still the old currency's untouched defaults move to the new currency's
 * defaults; anything the host set by hand is left alone.
 * @returns {{ ok: true, updates: Object } | { ok: false, reason: string }}
 */
export function planCurrencyChange(roomData, code) {
  if (!roomData) return { ok: false, reason: 'Room not found' };
  if (isCurrencyLocked(roomData)) {
    return { ok: false, reason: 'Currency is locked once the first hand is dealt.' };
  }
  const next = resolveCurrency(code);
  const settings = roomData.settings || {};
  const prev = resolveCurrency(settings.currency);
  const updates = { 'settings/currency': next };
  if (next === prev) return { ok: true, updates };

  const oldBlinds = getDefaultBlinds(prev);
  if (settings.smallBlind === oldBlinds.smallBlind && settings.bigBlind === oldBlinds.bigBlind) {
    const nb = getDefaultBlinds(next);
    updates['settings/smallBlind'] = nb.smallBlind;
    updates['settings/bigBlind'] = nb.bigBlind;
  }
  const oldBuyIn = getDefaultBuyInCents(prev);
  const newBuyIn = getDefaultBuyInCents(next);
  Object.entries(roomData.players || {}).forEach(([id, pl]) => {
    if (pl && pl.stack === oldBuyIn && (pl.totalInvestment ?? oldBuyIn) === oldBuyIn) {
      updates[`players/${id}/stack`] = newBuyIn;
      updates[`players/${id}/totalInvestment`] = newBuyIn;
    }
  });
  return { ok: true, updates };
}

/** Host-only currency change; refused once the first hand is dealt. */
export async function setRoomCurrency(roomCode, hostId, code) {
  const roomRef = ref(database, `rooms/${roomCode}`);
  const snapshot = await get(roomRef);
  if (!snapshot.exists()) throw new Error('Room not found');
  const roomData = snapshot.val();
  await requireHostCaller(roomData, hostId);
  const plan = planCurrencyChange(roomData, code);
  if (!plan.ok) return plan;
  await update(roomRef, plan.updates);
  await updateRoomActivity(roomCode);
  return { ok: true };
}

/**
 * Clean up inactive rooms (called periodically)
 * Removes rooms that haven't had activity in the last 10 minutes
 */
export async function cleanupInactiveRooms() {
  const INACTIVE_TIMEOUT = 10 * 60 * 1000; // 10 minutes in milliseconds
  const now = Date.now();
  
  try {
    console.log('🧹 Starting room cleanup...');
    
    const roomsRef = ref(database, 'rooms');
    const snapshot = await get(roomsRef);
    
    if (!snapshot.exists()) {
      console.log('🧹 No rooms to clean up');
      return;
    }
    
    const rooms = snapshot.val();
    const roomsToDelete = [];
    
    // Check each room for inactivity
    Object.entries(rooms).forEach(([roomCode, roomData]) => {
      const lastActivity = roomData.lastActivity || roomData.createdAt;
      const timeSinceActivity = now - lastActivity;
      
      if (timeSinceActivity > INACTIVE_TIMEOUT) {
        roomsToDelete.push(roomCode);
        console.log(`🧹 Room ${roomCode} inactive for ${Math.round(timeSinceActivity / 60000)} minutes, marking for deletion`);
      }
    });
    
    // Delete inactive rooms
    for (const roomCode of roomsToDelete) {
      await remove(ref(database, `rooms/${roomCode}`));
      console.log(`🧹 Deleted inactive room: ${roomCode}`);
    }
    
    if (roomsToDelete.length > 0) {
      console.log(`🧹 Cleanup complete: Removed ${roomsToDelete.length} inactive rooms`);
    } else {
      console.log('🧹 Cleanup complete: No inactive rooms found');
    }
  } catch (error) {
    console.error('Failed to cleanup inactive rooms:', error);
  }
}

/**
 * End the game session — writes status:'ENDED' to the top-level room document
 * (so Home.jsx detects it) and updates the nested gameState simultaneously.
 */
export async function endGameSession(roomCode, gameStateUpdate, hostId) {
  try {
    const roomRef = ref(database, `rooms/${roomCode}`);
    const snapshot = await get(roomRef);
    if (!snapshot.exists()) throw new Error('Room not found');
    const roomData = snapshot.val();
    await requireHostCaller(roomData, hostId);

    // Mid-hand End & Settle: return pot/street chips to contributors so
    // settlement sees every dollar. Do not award the unfinished pot.
    const voided = voidUnfinishedHand(roomData.players || {});
    const playerUpdates = {};
    Object.entries(voided.players).forEach(([id, player]) => {
      playerUpdates[`players/${id}`] = toStoredPlayer(player);
    });
    const nextGame = { ...gameStateUpdate };
    if (voided.voided) {
      nextGame.pots = [];
      nextGame.currentTurnPlayerId = null;
    }

    await update(roomRef, {
      status: 'ENDED',
      lastActivity: Date.now(),
      ...playerUpdates,
    });
    await set(ref(database, `rooms/${roomCode}/gameState`), nextGame);
    console.log(`🏁 Game session ended for room ${roomCode}`);
  } catch (error) {
    console.error('Failed to end game session:', error);
    throw error;
  }
}

/**
 * Update game state (host only)
 * @param {string} roomCode - Room code
 * @param {Object} gameStateUpdate - Updated game state object
 * @param {string} hostId - Host ID for verification
 */
export async function updateGameState(roomCode, gameStateUpdate, hostId, allowPlayerAction = false) {
  try {
    // Verify room exists
    const roomRef = ref(database, `rooms/${roomCode}`);
    const snapshot = await get(roomRef);
    
    if (!snapshot.exists()) {
      throw new Error('Room not found');
    }
    
    const roomData = snapshot.val();
    
    // Player actions may write game state. Host-only writes must be the signed-in host.
    if (!allowPlayerAction) {
      await requireHostCaller(roomData, hostId);
    }
    
    // Update game state
    await set(ref(database, `rooms/${roomCode}/gameState`), gameStateUpdate);
    await updateRoomActivity(roomCode);
    
    console.log(`🎮 Game state updated for room ${roomCode}:`, gameStateUpdate);
  } catch (error) {
    console.error('Failed to update game state:', error);
    throw error;
  }
}

/**
 * Attach the winner's callout to the current hand's winnerInfo (presentation only).
 *
 * Written from the WINNER's device (who may not be the host), so it uses a
 * scoped update() at gameState/winnerInfo/callout instead of rewriting the
 * whole gameState node — it can never clobber a concurrent host write, and it
 * is cleared automatically when winnerInfo is cleared on the next hand.
 *
 * Clients that dismissed the winner modal ("Got it") before the callout arrives
 * re-open it automatically — the dismissal key in GameBoard.jsx includes
 * callout.ts, so a new callout invalidates the dismissal.
 *
 * TODO(callouts): if room security rules are ever tightened to host-only
 * gameState writes, this path needs a rules exception.
 *
 * @param {string} roomCode - Room code
 * @param {Object} callout - { targetId, targetName, mode, index, ts }
 */
export async function sendWinnerCallout(roomCode, callout) {
  try {
    const winnerInfoRef = ref(database, `rooms/${roomCode}/gameState/winnerInfo`);
    const snapshot = await get(winnerInfoRef);

    // Only attach to a live winnerInfo (must have winnerIds) — if the host
    // already dealt the next hand, the moment has passed and the callout is
    // dropped rather than recreating a callout-only winnerInfo node.
    const current = snapshot.exists() ? snapshot.val() : null;
    if (!current || !Array.isArray(current.winnerIds) || current.winnerIds.length === 0) {
      console.warn('💬 Callout skipped — no active winnerInfo');
      return;
    }

    await update(winnerInfoRef, { callout });
    console.log(`💬 Callout sent in room ${roomCode}:`, { targetId: callout.targetId, mode: callout.mode });
  } catch (error) {
    console.error('Failed to send callout:', error);
    // Presentation-only feature — never break gameplay over a callout.
  }
}

/**
 * Update players state (host only)
 * @param {string} roomCode - Room code
 * @param {Object} playersUpdate - Updated players object
 * @param {string} hostId - Host ID for verification
 */
/**
 * The exact shape a player is persisted in. Kept as one function because the
 * undo snapshot must capture precisely the fields an action can change — if a
 * field is written here but missing from a snapshot, undo would silently fail
 * to restore it.
 */
export function toStoredPlayer(p) {
  const stored = {
    id: p.id,
    name: p.name,
    isHost: p.isHost,
    // Legacy flag: always true now that joins skip approval. Kept so a client
    // still running an older bundle mid-rollout doesn't hide new joiners.
    isApproved: true,
    stack: p.stack,
    totalInvestment: p.totalInvestment || 0, // Preserve investment tracking
    role: p.role,
    status: p.status,
    currentRoundBet: p.currentRoundBet || 0,
    totalPotContribution: p.totalPotContribution || 0,
    hasFolded: p.hasFolded || false,
    isActive: p.isActive,
    isAllIn: p.isAllIn || false,
    hasActedThisRound: p.hasActedThisRound || false,
    joinedAt: p.joinedAt
  };
  // Optional fields — omit vs null so a reclaim wipes leftover cash-out keys
  // (RTDB deletes nulls) without inventing them on older live player records.
  if (p.presence) stored.presence = p.presence;
  if (p.lastChipUpdate) stored.lastChipUpdate = p.lastChipUpdate;
  if (p.controllerUid) stored.controllerUid = p.controllerUid;
  if (p.reclaimedAt) stored.reclaimedAt = p.reclaimedAt;
  stored.rejoinToken = p.rejoinToken || null;
  stored.away = p.away === true ? true : null;
  stored.sitOutPending = p.sitOutPending === true ? true : null;
  stored.missedSbCount = p.missedSbCount > 0 ? p.missedSbCount : null;
  stored.missedBbCount = p.missedBbCount > 0 ? p.missedBbCount : null;
  stored.missedBlindPostCents = p.missedBlindPostCents > 0 ? p.missedBlindPostCents : null;
  if (p.status === 'left') {
    stored.cashedOut = p.cashedOut ?? p.stack ?? 0;
    if (p.leftAt) stored.leftAt = p.leftAt;
  } else {
    stored.cashedOut = null;
    stored.leftAt = null;
  }
  return stored;
}

/**
 * Firebase `update({ players: map })` replaces the whole players node and
 * deletes anyone not in `map` — including a pending joiner who arrived after
 * this client last rendered. Per-player paths merge instead.
 */
export function playerPathUpdates(playersObject) {
  const updates = {};
  Object.entries(playersObject || {}).forEach(([id, player]) => {
    updates[`players/${id}`] = player;
  });
  return updates;
}

/** Sit out / I'm back. Deal owns missed-blind counters; an action must not. */
export const AWAY_STATUS_KEYS = ['away', 'sitOutPending'];
export const MISSED_BLIND_KEYS = ['missedSbCount', 'missedBbCount', 'missedBlindPostCents'];
export const AWAY_PLAYER_KEYS = [...AWAY_STATUS_KEYS, ...MISSED_BLIND_KEYS];

function livePlayerMap(livePlayers) {
  if (livePlayers && typeof livePlayers === 'object' && !Array.isArray(livePlayers)) {
    return livePlayers;
  }
  return Object.fromEntries((livePlayers || []).filter((p) => p?.id).map((p) => [p.id, p]));
}

function shouldWriteAwayKeys(awayFlags, id, player, actorId, live) {
  if (awayFlags === 'all') return true;
  if (awayFlags === 'omit') return false;
  if (id === actorId) return true;
  // This action ended their cards: pending → Away. I'm back already cleared
  // live.sitOutPending, so a stale local pending must not restore Away.
  const livePending = live?.sitOutPending === true;
  const localAway = player?.away === true;
  const localPending = player?.sitOutPending === true;
  return livePending && localAway && !localPending;
}

/**
 * Nested `players/{id}/{field}` writes. Firebase `update({ players/id: obj })`
 * replaces the whole seat — `away: null` deletes I'm back, and a stale
 * `away: true` puts someone back on Away after they tapped I'm back.
 *
 * `awayFlags`:
 *   'all'   — every key (Deal)
 *   'omit'  — never Away / missed-blind keys (street betting reset)
 *   'actor' — Away status for the actor, and for live pending seats this
 *             action promotes to Away. Missed-blind counters stay untouched.
 */
export function playerFieldUpdates(playersObject, { awayFlags = 'all', actorId, livePlayers } = {}) {
  const liveMap = livePlayerMap(livePlayers);
  const updates = {};
  Object.entries(playersObject || {}).forEach(([id, player]) => {
    if (!player || typeof player !== 'object') return;
    const writeAway = shouldWriteAwayKeys(awayFlags, id, player, actorId, liveMap[id]);
    Object.entries(player).forEach(([key, value]) => {
      // Missed blinds are Deal-only. An auto-fold actor write of null
      // would delete the counters an Away seat just accrued.
      if (awayFlags !== 'all' && MISSED_BLIND_KEYS.includes(key)) return;
      if (!writeAway && AWAY_STATUS_KEYS.includes(key)) return;
      updates[`players/${id}/${key}`] = value;
    });
  });
  return updates;
}

/** The exact shape gameState is persisted in. Same contract as toStoredPlayer. */
export function toStoredGameState(gs) {
  return {
    stage: gs.stage,
    dealerId: gs.dealerId,
    currentTurnPlayerId: gs.currentTurnPlayerId ?? null,
    handNumber: gs.handNumber,
    pots: gs.pots || [],
    canAdvanceStage: gs.canAdvanceStage || false,
    nextStage: gs.nextStage || null,
    nextStageName: gs.nextStageName || null,
    stageProgress: gs.stageProgress || 0,
    lastRaiseAmount: gs.lastRaiseAmount || 0,
    incompleteRaise: gs.incompleteRaise || false,
    roundActions: gs.roundActions || [],
    // All-in runouts set this so the table can show "Betting complete" after a
    // refresh. Null so a later write deletes a leftover from the previous hand.
    autoAdvancedReason: gs.autoAdvancedReason || null,
    winnerInfo: gs.winnerInfo || null,
    // Drives the fold/check/call/raise sound on every phone (actionSounds.js).
    lastAction: gs.lastAction || null
  };
}

/**
 * Reverse the most recent gameplay action by restoring the snapshot taken just
 * before it. Host only, one level deep, current hand only.
 *
 * @param {string} roomCode
 * @param {string} hostId - must match the room's host
 * @param {number} expectedTs - the snapshot the host actually saw and confirmed
 * @returns {Object} { ok: true, description } or { ok: false, reason }
 */
export async function undoLastAction(roomCode, hostId, expectedTs) {
  const roomRef = ref(database, `rooms/${roomCode}`);
  const snapshot = await get(roomRef);

  if (!snapshot.exists()) throw new Error('Room not found');
  const roomData = snapshot.val();

  {
    const denied = await hostCallerOrDeny(roomData, hostId, 'Only the host can undo.');
    if (!denied.ok) return denied;
  }

  const undo = roomData.undoSnapshot;
  if (!undo || !undo.gameState || !undo.players) {
    return { ok: false, reason: 'Nothing to undo.' };
  }

  // Re-read guard: another player can act between the host opening the confirm
  // dialog and tapping Undo. Without this we would restore a snapshot older
  // than the action the button named, silently reverting two actions.
  if (expectedTs != null && undo.ts !== expectedTs) {
    return { ok: false, reason: 'Someone acted just now — check the table and try again.' };
  }

  // Belt and braces: a snapshot from a previous hand must never be applied.
  const liveHandNumber = roomData.gameState?.handNumber;
  if (liveHandNumber != null && undo.handNumber !== liveHandNumber) {
    return { ok: false, reason: 'That action was from an earlier hand.' };
  }

  // Restore per-player rather than replacing the whole players node: someone may
  // have joined since the snapshot, and a wholesale write would delete them.
  const updates = {
    gameState: toStoredGameState(undo.gameState),
    undoSnapshot: null,
    lastActivity: Date.now()
  };
  Object.entries(undo.players).forEach(([id, player]) => {
    updates[`players/${id}`] = toStoredPlayer(player);
  });

  await update(roomRef, updates);

  console.log(`↩️ Undid: ${undo.description}`);
  return { ok: true, description: undo.description };
}

/**
 * Drop the pending undo. Called when the snapshot stops being safe to apply:
 * a new hand starts, or the host manually awards a pot (where restoring a
 * pre-showdown snapshot would silently un-award the pot they just confirmed).
 * Call this BEFORE the write it guards, so a failure aborts rather than leaving
 * a stale snapshot behind a completed award.
 */
/**
 * Deal a new hand in ONE atomic write: new gameState, every player's fresh
 * hand fields (roles, blinds, bets), cleared undo, and any blind-timer
 * settings. Before this, the deal was three writes (gameState, then a get(),
 * then players). In that gap every phone already saw "Hand #2 — your turn"
 * on top of LAST hand's players, and a quick Check/Call was processed against
 * them: the stale write put hand-1 stacks and roles back, so hand #2 played
 * with no blinds and a $0 pot, then stuck at Showdown with no Declare Winner.
 * A slow host connection widens the gap to seconds.
 */
export async function writeDealtHand(roomCode, hostId, { gameState, players, extraUpdates = {} }) {
  const roomRef = ref(database, `rooms/${roomCode}`);
  const snapshot = await get(roomRef);
  if (!snapshot.exists()) throw new Error('Room not found');
  await requireHostCaller(snapshot.val(), hostId);
  await update(roomRef, {
    ...extraUpdates,
    ...playerFieldUpdates(players, { awayFlags: 'all' }),
    gameState,
    undoSnapshot: null,
    lastActivity: Date.now(),
  });
}

export async function clearUndoSnapshot(roomCode) {
  await set(ref(database, `rooms/${roomCode}/undoSnapshot`), null);
}

/**
 * Pure transfer of `amount` cents from one player to another. Host chip
 * corrections only — never touches `totalInvestment`, because no cash changed
 * hands. Does not mutate `players`.
 *
 * @param {Object} players - Firebase players map keyed by id
 * @param {string} fromId
 * @param {string} toId
 * @param {number} amount - cents; must be a positive finite number
 * @returns {{ ok: true, fromStack: number, toStack: number, fromName: string, toName: string, players: Object } | { ok: false, reason: string }}
 */
export function applyChipMove(players, fromId, toId, amount, currency) {
  if (!fromId || !toId || fromId === toId) {
    return { ok: false, reason: 'Choose two different players' };
  }
  if (!Number.isFinite(amount) || amount <= 0) {
    return { ok: false, reason: 'Please enter a valid positive amount' };
  }

  const from = players?.[fromId];
  const to = players?.[toId];
  if (!from || !to) {
    return { ok: false, reason: 'Choose two different players' };
  }

  const fromStack = from.stack || 0;
  if (fromStack < amount) {
    return { ok: false, reason: `${from.name} only has ${formatCurrency(fromStack, currency)}.` };
  }

  const toStack = (to.stack || 0) + amount;
  const nextFromStack = fromStack - amount;

  return {
    ok: true,
    fromStack: nextFromStack,
    toStack,
    fromName: from.name,
    toName: to.name,
    players: {
      ...players,
      [fromId]: { ...from, stack: nextFromStack },
      [toId]: { ...to, stack: toStack }
    }
  };
}

/**
 * Host-only chip transfer. Re-reads live stacks from Firebase, then writes
 * both sides in one update() so the move can never half-apply. UI still has
 * to lock the confirm button — this does not by itself stop a second
 * transfer after the first has landed.
 *
 * @param {string} roomCode
 * @param {string} hostId
 * @param {{ fromId: string, toId: string, amount: number }} move
 * @returns {Promise<{ ok: true, fromName: string, toName: string, amount: number } | { ok: false, reason: string }>}
 */
export async function moveChips(roomCode, hostId, { fromId, toId, amount }) {
  const roomRef = ref(database, `rooms/${roomCode}`);
  const snapshot = await get(roomRef);

  if (!snapshot.exists()) throw new Error('Room not found');
  const roomData = snapshot.val();

  {
    const denied = await hostCallerOrDeny(roomData, hostId, 'Only the host can move chips.');
    if (!denied.ok) return denied;
  }

  const result = applyChipMove(roomData.players, fromId, toId, amount, roomData.settings?.currency);
  if (!result.ok) return result;

  const now = Date.now();
  await update(roomRef, {
    [`players/${fromId}/stack`]: result.fromStack,
    [`players/${fromId}/lastChipUpdate`]: now,
    [`players/${toId}/stack`]: result.toStack,
    [`players/${toId}/lastChipUpdate`]: now,
    lastActivity: now
  });

  console.log(`🔀 Moved ${formatCurrency(amount)} from ${result.fromName} to ${result.toName}`);
  return { ok: true, fromName: result.fromName, toName: result.toName, amount };
}

/**
 * Pure rebuy: add `amount` cents to one player's stack AND totalInvestment.
 * Cash is coming onto the table, so both must move together or settle-up
 * shows a profit nobody is asked to pay. Does not mutate `players`.
 *
 * @param {Object} players - Firebase players map keyed by id
 * @param {string} playerId
 * @param {number} amount - cents; must be a positive finite number
 * @returns {{ ok: true, stack: number, totalInvestment: number, name: string, players: Object } | { ok: false, reason: string }}
 */
export function applyRebuy(players, playerId, amount) {
  if (!playerId) {
    return { ok: false, reason: 'Selected player not found' };
  }
  if (!Number.isFinite(amount) || amount <= 0) {
    return { ok: false, reason: 'Please enter a valid positive amount' };
  }

  const player = players?.[playerId];
  if (!player) {
    return { ok: false, reason: 'Selected player not found' };
  }

  const stack = (player.stack || 0) + amount;
  const totalInvestment = (player.totalInvestment || 0) + amount;

  return {
    ok: true,
    stack,
    totalInvestment,
    name: player.name,
    players: {
      ...players,
      [playerId]: { ...player, stack, totalInvestment }
    }
  };
}

/**
 * Host-only rebuy. Re-reads live stacks from Firebase, then writes stack,
 * totalInvestment, and lastChipUpdate in one update() so the add cannot
 * half-apply. UI still has to lock the confirm button.
 *
 * @param {string} roomCode
 * @param {string} hostId
 * @param {{ playerId: string, amount: number }} rebuy
 * @returns {Promise<{ ok: true, name: string, amount: number } | { ok: false, reason: string }>}
 */
export async function rebuyChips(roomCode, hostId, { playerId, amount }) {
  const roomRef = ref(database, `rooms/${roomCode}`);
  const snapshot = await get(roomRef);

  if (!snapshot.exists()) throw new Error('Room not found');
  const roomData = snapshot.val();

  {
    const denied = await hostCallerOrDeny(roomData, hostId, 'Only the host can process a rebuy.');
    if (!denied.ok) return denied;
  }

  const result = applyRebuy(roomData.players, playerId, amount);
  if (!result.ok) return result;

  const now = Date.now();
  const updates = {
    [`players/${playerId}/stack`]: result.stack,
    [`players/${playerId}/totalInvestment`]: result.totalInvestment,
    [`players/${playerId}/lastChipUpdate`]: now,
    lastActivity: now
  };
  if (roomData.rebuyRequests?.[playerId]) {
    updates[`rebuyRequests/${playerId}`] = null;
  }
  await update(roomRef, updates);

  console.log(`💰 Rebuy processed: +${formatCurrency(amount)} for ${result.name}`);
  return { ok: true, name: result.name, amount };
}

/**
 * Player asks the host to add chips. Own seat only. Amount is cents.
 * Overwrites a previous pending request. Does not mutate `roomData`.
 */
export function planRequestRebuy(roomData, playerId, amount, now = Date.now()) {
  if (!roomData) return { ok: false, reason: 'Room not found' };
  if (roomData.status === 'ENDED') {
    return { ok: false, reason: 'This game has already ended.' };
  }
  const player = roomData.players?.[playerId];
  if (!isSeated(player)) {
    return { ok: false, reason: 'Player not found' };
  }
  if (!Number.isFinite(amount) || amount <= 0) {
    return { ok: false, reason: 'Please enter a valid positive amount' };
  }
  if ((player.stack || 0) > 0) {
    return { ok: false, reason: 'Rebuy from your phone is for when you have no chips' };
  }
  return {
    ok: true,
    updates: {
      [`rebuyRequests/${playerId}/amount`]: amount,
      [`rebuyRequests/${playerId}/requestedAt`]: now,
      lastActivity: now,
    },
  };
}

/** Withdraw a pending phone-rebuy. Own seat only. No-ops if already gone. */
export function planCancelRebuy(roomData, playerId, now = Date.now()) {
  if (!roomData) return { ok: false, reason: 'Room not found' };
  const player = roomData.players?.[playerId];
  if (!isSeated(player) && !roomData.rebuyRequests?.[playerId]) {
    return { ok: false, reason: 'Player not found' };
  }
  if (!roomData.rebuyRequests?.[playerId]) {
    return { ok: true, alreadyGone: true, updates: { lastActivity: now } };
  }
  return {
    ok: true,
    updates: {
      [`rebuyRequests/${playerId}`]: null,
      lastActivity: now,
    },
  };
}

/**
 * Host applies a pending phone-rebuy: same stack + totalInvestment write as
 * applyRebuy, then clears the request. Re-reads must happen in the async
 * wrapper. Does not mutate.
 */
export function planAddPendingRebuy(roomData, playerId, now = Date.now()) {
  if (!roomData) return { ok: false, reason: 'Room not found' };
  const req = roomData.rebuyRequests?.[playerId];
  if (!req || !Number.isFinite(req.amount) || req.amount <= 0) {
    return { ok: false, reason: 'That rebuy request is no longer there' };
  }
  const result = applyRebuy(roomData.players, playerId, req.amount);
  if (!result.ok) return result;
  return {
    ok: true,
    amount: req.amount,
    name: result.name,
    stack: result.stack,
    totalInvestment: result.totalInvestment,
    updates: {
      [`players/${playerId}/stack`]: result.stack,
      [`players/${playerId}/totalInvestment`]: result.totalInvestment,
      [`players/${playerId}/lastChipUpdate`]: now,
      [`rebuyRequests/${playerId}`]: null,
      lastActivity: now,
    },
  };
}

export async function requestRebuy(roomCode, playerId, amount) {
  const uid = await ensureAuth();
  const roomRef = ref(database, `rooms/${roomCode}`);
  const snapshot = await get(roomRef);
  if (!snapshot.exists()) throw new Error('Room not found');
  const roomData = snapshot.val();
  const player = roomData.players?.[playerId];
  if (!seatCallerAllowed(player, uid)) {
    throw new Error('Only you can request a rebuy for your seat');
  }
  const plan = planRequestRebuy(roomData, playerId, amount);
  if (!plan.ok) return plan;
  await update(roomRef, plan.updates);
  return plan;
}

export async function cancelRebuyRequest(roomCode, playerId) {
  const uid = await ensureAuth();
  const roomRef = ref(database, `rooms/${roomCode}`);
  const snapshot = await get(roomRef);
  if (!snapshot.exists()) throw new Error('Room not found');
  const roomData = snapshot.val();
  const player = roomData.players?.[playerId];
  if (!seatCallerAllowed(player, uid) && roomData.rebuyRequests?.[playerId]) {
    throw new Error('Only you can cancel your rebuy request');
  }
  const plan = planCancelRebuy(roomData, playerId);
  if (!plan.ok) return plan;
  if (!plan.alreadyGone) await update(roomRef, plan.updates);
  return plan;
}

export async function addPendingRebuy(roomCode, hostId, playerId) {
  const roomRef = ref(database, `rooms/${roomCode}`);
  const snapshot = await get(roomRef);
  if (!snapshot.exists()) throw new Error('Room not found');
  const roomData = snapshot.val();

  {
    const denied = await hostCallerOrDeny(roomData, hostId, 'Only the host can process a rebuy.');
    if (!denied.ok) return denied;
  }

  const plan = planAddPendingRebuy(roomData, playerId);
  if (!plan.ok) return plan;
  await update(roomRef, plan.updates);
  console.log(`💰 Rebuy processed: +${formatCurrency(plan.amount)} for ${plan.name}`);
  return { ok: true, name: plan.name, amount: plan.amount };
}

/**
 * Lobby buy-in correction: SET stack and totalInvestment to the same amount.
 * Uneven buy-ins are a host table-stakes fix before the first hand — both
 * must move together or settlement shows a profit nobody is asked to pay.
 * Does not mutate `players`. Same player paths as rebuy (`stack`,
 * `totalInvestment`, `lastChipUpdate`); no new Firebase node.
 *
 * @param {Object} players
 * @param {string} playerId
 * @param {number} amountCents - amount already in cents (decimals allowed)
 * @returns {{ ok: true, stack: number, totalInvestment: number, name: string, players: Object } | { ok: false, reason: string }}
 */
export function applyLobbyBuyIn(players, playerId, amountCents) {
  if (!playerId) {
    return { ok: false, reason: 'Selected player not found' };
  }
  if (!isValidBetAmount(amountCents, 100, MAX_BET_CENTS)) {
    return { ok: false, reason: 'Please enter a valid amount' };
  }

  const player = players?.[playerId];
  if (!player) {
    return { ok: false, reason: 'Selected player not found' };
  }

  return {
    ok: true,
    stack: amountCents,
    totalInvestment: amountCents,
    name: player.name,
    players: {
      ...players,
      [playerId]: { ...player, stack: amountCents, totalInvestment: amountCents }
    }
  };
}

/**
 * Host-only lobby stack edit. Re-reads live Firebase, then writes stack,
 * totalInvestment, and lastChipUpdate in one update() — the same paths as
 * rebuy. Lobby only; mid-game uses Move chips / Rebuy.
 *
 * @param {string} roomCode
 * @param {string} hostId
 * @param {{ playerId: string, amount: number }} edit - amount in cents
 * @returns {Promise<{ ok: true, name: string, amount: number } | { ok: false, reason: string }>}
 */
export async function setLobbyBuyIn(roomCode, hostId, { playerId, amount }) {
  const roomRef = ref(database, `rooms/${roomCode}`);
  const snapshot = await get(roomRef);

  if (!snapshot.exists()) throw new Error('Room not found');
  const roomData = snapshot.val();

  {
    const denied = await hostCallerOrDeny(roomData, hostId, 'Only the host can edit stacks.');
    if (!denied.ok) return denied;
  }

  if (roomData.status && roomData.status !== 'LOBBY') {
    return { ok: false, reason: 'Stacks can only be edited in the lobby.' };
  }

  const result = applyLobbyBuyIn(roomData.players, playerId, amount);
  if (!result.ok) return result;

  const now = Date.now();
  await update(roomRef, {
    [`players/${playerId}/stack`]: result.stack,
    [`players/${playerId}/totalInvestment`]: result.totalInvestment,
    [`players/${playerId}/lastChipUpdate`]: now,
    lastActivity: now
  });

  console.log(`✏️ Lobby buy-in set: ${formatCurrency(amount)} for ${result.name}`);
  return { ok: true, name: result.name, amount };
}

/**
 * Table-wide lobby buy-in: the host always moves, and every other seated
 * player still on the previous table default moves with them. Anyone the
 * host (or they themselves) already set to a different amount is left alone.
 * Does not mutate `players`.
 */
export function applyTableBuyIn(players, hostId, amountCents) {
  if (!isValidBetAmount(amountCents, 100, MAX_BET_CENTS)) {
    return { ok: false, reason: 'Please enter a valid amount' };
  }
  const oldDefault = tableDefaultBuyIn(players, hostId);
  const next = {};
  const changed = [];
  Object.entries(players || {}).forEach(([id, player]) => {
    if (!isSeated(player)) {
      next[id] = player;
      return;
    }
    const onDefault = player.id === hostId
      || Number(player.totalInvestment) === oldDefault
      || Number(player.stack) === oldDefault;
    if (!onDefault) {
      next[id] = player;
      return;
    }
    next[id] = { ...player, stack: amountCents, totalInvestment: amountCents };
    changed.push(id);
  });
  if (changed.length === 0) {
    return { ok: false, reason: 'Selected player not found' };
  }
  return { ok: true, amount: amountCents, changed, players: next };
}

/**
 * Host-only: set the table buy-in. Writes stack + totalInvestment for the
 * host and every lobby seat still on the old default.
 */
export async function setTableBuyIn(roomCode, hostId, amount) {
  const roomRef = ref(database, `rooms/${roomCode}`);
  const snapshot = await get(roomRef);

  if (!snapshot.exists()) throw new Error('Room not found');
  const roomData = snapshot.val();

  {
    const denied = await hostCallerOrDeny(roomData, hostId, 'Only the host can edit stacks.');
    if (!denied.ok) return denied;
  }

  if (roomData.status && roomData.status !== 'LOBBY') {
    return { ok: false, reason: 'Stacks can only be edited in the lobby.' };
  }

  const result = applyTableBuyIn(roomData.players, roomData.hostId, amount);
  if (!result.ok) return result;

  const now = Date.now();
  const updates = { lastActivity: now };
  result.changed.forEach((playerId) => {
    const player = result.players[playerId];
    updates[`players/${playerId}/stack`] = player.stack;
    updates[`players/${playerId}/totalInvestment`] = player.totalInvestment;
    updates[`players/${playerId}/lastChipUpdate`] = now;
  });
  await update(roomRef, updates);

  console.log(`✏️ Table buy-in set: ${formatCurrency(amount)} for ${result.changed.length} seat(s)`);
  return { ok: true, amount, changed: result.changed };
}

export async function updatePlayers(roomCode, playersUpdate, hostId, allowPlayerAction = false, writeOptions = null) {
  try {
    // Verify room exists
    const roomRef = ref(database, `rooms/${roomCode}`);
    const snapshot = await get(roomRef);
    
    if (!snapshot.exists()) {
      throw new Error('Room not found');
    }
    
    const roomData = snapshot.val();
    
    if (!allowPlayerAction) {
      await requireHostCaller(roomData, hostId);
    }
    
    // Per-player paths so a joiner who arrived after this snapshot is not
    // deleted. A wholesale `set(players)` is what hid mid-game pending seats.
    // Optional field-level writes omit Sit out / I'm back keys so an award
    // cannot restore Away after I'm back.
    const playerUpdates = writeOptions?.awayFlags
      ? playerFieldUpdates(playersUpdate, writeOptions)
      : playerPathUpdates(playersUpdate);
    await update(roomRef, {
      ...playerUpdates,
      lastActivity: Date.now(),
    });
    
    console.log(`👥 Players updated for room ${roomCode}:`, playersUpdate);
  } catch (error) {
    console.error('Failed to update players:', error);
    throw error;
  }
}

/**
 * Process a player action immediately using pokerLogic.js
 * @param {Object} gameState - Current game state
 * @param {Array} players - Current players array
 * @param {string} playerId - Player performing the action
 * @param {Object} action - Action object { type, amount? }
 * @param {Object} settings - Game settings (for bigBlind, etc.)
 * @returns {Object} Updated game state and players
 */
export function processAction(gameState, players, playerId, action, settings) {
  const { type, amount = 0 } = action;
  const player = players.find(p => p.id === playerId);
  
  console.log(`🎮 Processing ${type} action from ${player?.name || playerId}`);
  console.log(`🎮 Current turn player: ${gameState.currentTurnPlayerId}, Acting player: ${playerId}`);
  console.log(`🎮 Game stage: ${gameState.stage}`);
  
  if (!player || !player.isActive || player.hasFolded) {
    console.log(`🎮 Invalid player state for action - isActive: ${player?.isActive}, hasFolded: ${player?.hasFolded}`);
    return null;
  }
  
  // Verify it's the player's turn
  if (gameState.currentTurnPlayerId !== playerId) {
    console.log(`🎮 Not player's turn: expected ${gameState.currentTurnPlayerId}, got ${playerId}`);
    return null;
  }
  
  let updatedPlayers = [...players];
  let updatedGameState = { ...gameState };

  // Last-action broadcast: what gets announced to every client after this action.
  // CALL with nothing owed is announced as a CHECK.
  let laType = type;
  let laAmount = 0;

  switch (type) {
    case 'FOLD':
      updatedPlayers = updatedPlayers.map(p => 
        p.id === playerId ? { ...p, hasFolded: true, hasActedThisRound: true } : p
      );
      break;
      
    case 'CALL':
      const currentHighestBetForCall = Math.max(...players.map(p => p.currentRoundBet || 0), 0);
      const callAmount = Math.max(0, currentHighestBetForCall - (player.currentRoundBet || 0));
      laType = callAmount === 0 ? 'CHECK' : 'CALL';
      laAmount = callAmount;

      if (callAmount > player.stack) {
        // All-in call
        const allInAmount = player.stack;
        updatedPlayers = updatedPlayers.map(p => 
          p.id === playerId ? { 
            ...p, 
            currentRoundBet: (p.currentRoundBet || 0) + allInAmount,
            totalPotContribution: (p.totalPotContribution || 0) + allInAmount,
            stack: 0,
            isAllIn: true,
            hasActedThisRound: true
          } : p
        );
      } else {
        // Regular call - add call amount to current bet
        updatedPlayers = updatedPlayers.map(p => 
          p.id === playerId ? { 
            ...p, 
            currentRoundBet: (p.currentRoundBet || 0) + callAmount,
            totalPotContribution: (p.totalPotContribution || 0) + callAmount,
            stack: p.stack - callAmount,
            hasActedThisRound: true
          } : p
        );
      }
      break;
      
    case 'RAISE':
      // amount now represents the TOTAL BET the player wants to make, not the raise amount
      const totalBetDesired = amount;
      const currentBet = player.currentRoundBet || 0;
      const additionalAmount = totalBetDesired - currentBet;
      const currentHighestBetForRaise = Math.max(...players.map(p => p.currentRoundBet || 0), 0);
      const raiseAmount = totalBetDesired - currentHighestBetForRaise;
      
      // Validate minimum raise according to Texas Hold'em rules
      const minRaise = getMinimumRaise(players, settings?.bigBlind || 200, gameState);

      // A short all-in does not reopen: players who have already acted may
      // only call or fold (TDA). Someone who has not yet acted can still raise.
      if (gameState.incompleteRaise && player.hasActedThisRound) {
        console.log('🎮 Invalid raise: incomplete all-in did not reopen betting');
        return null;
      }
      
      if (raiseAmount < minRaise) {
        console.log(`🎮 Invalid raise: ${raiseAmount} < minimum ${minRaise}`);
        return null; // Invalid raise amount
      }

      // You cannot wager more than the biggest opponent is able to call — the
      // excess could never be won, only handed back. RaisePanel already clamps
      // the slider to this; enforcing it here too means a stale client or a
      // replayed action cannot slip past it.
      const maxEffectiveBet = getMaximumRaise(players, playerId);
      if (totalBetDesired > maxEffectiveBet) {
        console.log(`🎮 Invalid raise: ${totalBetDesired} > effective maximum ${maxEffectiveBet}`);
        return null;
      }

      laType = 'RAISE';
      laAmount = totalBetDesired;

      if (additionalAmount > player.stack) {
        // All-in raise - player bets everything they have
        const allInAmount = player.stack;
        const newTotalBet = currentBet + allInAmount;
        const actualRaise = newTotalBet - currentHighestBetForRaise;
        const fullRaise = actualRaise >= minRaise;
        
        updatedPlayers = updatedPlayers.map(p => 
          p.id === playerId ? { 
            ...p, 
            currentRoundBet: newTotalBet,
            totalPotContribution: (p.totalPotContribution || 0) + allInAmount,
            stack: 0,
            isAllIn: true,
            hasActedThisRound: true
          } : p
        );

        if (fullRaise) {
          updatedPlayers = updatedPlayers.map(p => 
            p.id !== playerId && !p.hasFolded && p.isActive && !p.isAllIn ? { ...p, hasActedThisRound: false } : p
          );
          updatedGameState.lastRaiseAmount = actualRaise;
          updatedGameState.incompleteRaise = false;
        } else {
          // Short of a full raise: others still call the extra, but cannot re-raise
          // if they have already acted. Keep the previous full-raise increment.
          updatedGameState.incompleteRaise = true;
        }
      } else {
        // Regular raise - player makes the desired total bet
        updatedPlayers = updatedPlayers.map(p => 
          p.id === playerId ? { 
            ...p, 
            currentRoundBet: totalBetDesired,
            totalPotContribution: (p.totalPotContribution || 0) + additionalAmount,
            stack: p.stack - additionalAmount,
            hasActedThisRound: true
          } : p
        );
        
        // Reset hasActedThisRound for other players (they need to respond to the raise)
        updatedPlayers = updatedPlayers.map(p => 
          p.id !== playerId && !p.hasFolded && p.isActive && !p.isAllIn ? { ...p, hasActedThisRound: false } : p
        );
        
        // Track the raise amount for future minimum raise calculations
        updatedGameState.lastRaiseAmount = raiseAmount;
        updatedGameState.incompleteRaise = false;
      }
      break;
      
    case 'ALL_IN':
      const currentHighestBetForAllIn = Math.max(...players.map(p => p.currentRoundBet || 0), 0);

      // Shoving more than the biggest opponent can call just creates chips that
      // have to be given back at the end of the hand, so cap it at the effective
      // stack. Unlike the raise slider, this button had no ceiling at all.
      const allInCeiling = getMaximumRaise(players, playerId);
      const newTotalBetAllIn = Math.min((player.currentRoundBet || 0) + player.stack, allInCeiling);
      const allInAmount = newTotalBetAllIn - (player.currentRoundBet || 0);
      const remainingStack = player.stack - allInAmount;

      // Capped short of their stack, this is a plain raise — say so rather than
      // announcing an all-in from a player who still has chips in front of them.
      // A cap that leaves nothing to add (every opponent already all-in for less
      // than this player's bet) is just a check.
      if (remainingStack === 0) laType = 'ALL_IN';
      else laType = allInAmount === 0 ? 'CHECK' : 'RAISE';
      laAmount = newTotalBetAllIn;

      updatedPlayers = updatedPlayers.map(p =>
        p.id === playerId ? {
          ...p,
          currentRoundBet: newTotalBetAllIn,
          totalPotContribution: (p.totalPotContribution || 0) + allInAmount,
          stack: remainingStack,
          isAllIn: remainingStack === 0,
          hasActedThisRound: true
        } : p
      );

      // Check if this all-in constitutes a full raise. A shove for less than
      // the minimum raise does not reopen betting for players who already acted.
      const allInRaiseAmount = newTotalBetAllIn - currentHighestBetForAllIn;
      const minRaiseForAllIn = getMinimumRaise(players, settings?.bigBlind || 200, gameState);
      const wasFullRaise = allInRaiseAmount >= minRaiseForAllIn;
      if (wasFullRaise) {
        updatedPlayers = updatedPlayers.map(p => 
          p.id !== playerId && !p.hasFolded && p.isActive && !p.isAllIn ? { ...p, hasActedThisRound: false } : p
        );
        updatedGameState.lastRaiseAmount = allInRaiseAmount;
        updatedGameState.incompleteRaise = false;
      } else if (allInRaiseAmount > 0) {
        updatedGameState.incompleteRaise = true;
      }
      break;
      
    default:
      console.log(`🎮 Unknown action type: ${type}`);
      return null;
  }

  // Store kind + amount, not a preformatted "$" string, so a mid-game
  // currency change re-formats the log on every phone. Old rooms still have
  // `{ text, ts }` entries; the UI renders those as stored.
  const prevRoundActions = Array.isArray(gameState.roundActions) ? gameState.roundActions : [];
  const logEntry = { kind: laType, name: player.name, ts: Date.now() };
  // Check/Fold pre-action: the player armed it before their turn, so the log
  // says it happened automatically.
  if (action.auto) logEntry.auto = true;
  if (laType === 'CALL' || laType === 'RAISE' || laType === 'ALL_IN') {
    logEntry.amount = laAmount;
  }
  updatedGameState.roundActions = [...prevRoundActions, logEntry];
  // Separate from roundActions (which a hand-ending fold clears in this same
  // write) so every phone can play the action sound exactly once.
  updatedGameState.lastAction = { kind: laType, playerId, ts: logEntry.ts };
  const actionDescription = formatRoundAction(
    logEntry,
    (cents) => formatCurrency(cents, settings?.currency),
    'en'
  );

  // Update pots based on new player states
  const updatedPots = calculateSidePotsFromState({ players: updatedPlayers });
  
  // 🎯 POT DEBUG - Action Processing (can be removed after testing)
  if (gameState.stage === 'PRE_FLOP') {
    console.log('🎯 ACTION POT UPDATE:', {
      action: type,
      potAmount: updatedPots.reduce((sum, pot) => sum + pot.amount, 0),
      totalPotContributions: updatedPlayers.reduce((sum, p) => sum + (p.totalPotContribution || 0), 0),
      currentRoundBetsTotal: updatedPlayers.reduce((sum, p) => sum + (p.currentRoundBet || 0), 0)
    });
  }
  
  // Ensure pots are clean objects without circular references
  const toCleanPots = pots => pots.map(pot => ({
    id: pot.id,
    amount: pot.amount || 0,
    eligiblePlayers: Array.isArray(pot.eligiblePlayers) ? [...pot.eligiblePlayers] : [],
    isMainPot: pot.isMainPot || false
  }));
  let cleanPots = toCleanPots(updatedPots);

  // Check if betting round is complete
  const isComplete = isBettingRoundComplete(updatedPlayers, null, gameState.stage, settings?.bigBlind || 200);

  if (isComplete) {
    console.log(`🎮 Betting round complete, checking for stage advancement`);

    // Check if only one player remains (everyone else folded)
    const activePlayers = updatedPlayers.filter(p => !p.hasFolded && p.isActive);

    // Use enhanced all-in detection to determine if we should auto-advance to showdown
    // IMPORTANT: read before any uncalled bet is returned. Handing chips back can
    // clear a player's isAllIn flag, and if that happened first this check would
    // decide betting was still live and deal another street to a player with
    // nobody left to bet against.
    const allInCheck = isNoBettingActionPossible(updatedPlayers);

    // The hand is over, so any part of the largest wager that nobody matched was
    // never at risk and goes straight back. Doing it here rather than at award
    // time means the pot the host sees is already the real contested pot, and the
    // unmatched chips never masquerade as a side pot.
    //
    // Only reachable on a terminal round: a lone top contributor at the end of
    // betting means everyone else has folded or is all-in for less.
    const lockHandAndReturnUncalledBet = () => {
      const { players: settled, refund } = returnUncalledBet(updatedPlayers);
      if (!refund) return null;

      updatedPlayers = settled;
      cleanPots = toCleanPots(calculateSidePotsFromState({ players: updatedPlayers }));
      return refund;
    };

    // Announcement for a return the host did not ask for and cannot undo by hand.
    const announceReturn = refund => {
      const name = updatedPlayers.find(p => p.id === refund.playerId)?.name || 'Player';
      updatedGameState.roundActions = [
        ...(updatedGameState.roundActions || []),
        { kind: 'RETURN', name, amount: refund.amount, ts: Date.now() }
      ];
    };

    if (allInCheck.noBetting && allInCheck.reason !== 'ONLY_ONE_PLAYER' && activePlayers.length > 1) {
      const refund = lockHandAndReturnUncalledBet();
      if (refund) announceReturn(refund);

      // No more betting action possible - automatically advance to showdown for manual winner declaration
      updatedGameState = {
        ...updatedGameState,
        stage: 'SHOWDOWN',
        currentTurnPlayerId: null,
        pots: cleanPots,
        canAdvanceStage: false,
        nextStage: null,
        stageProgress: getStageProgress('SHOWDOWN'),
        autoAdvancedReason: allInCheck.reason // Track why we auto-advanced
      };
      console.log(`🎮 AUTO-ADVANCING TO SHOWDOWN: ${allInCheck.reason}`);
      console.log(`🎮 Active players: ${activePlayers.length}, Reason: ${allInCheck.reason}`);
    } else if (activePlayers.length === 1) {
      // Hand is over - winner by fold
      // Award the pot to the remaining player immediately
      const winner = activePlayers[0];

      // Return the unmatched part of the last wager before settling pots. The
      // winner is already known here, so when the chips are going back to that
      // same player there is nothing to explain — the usual case is the big
      // blind whose blind nobody called. Fold it into their winnings and stay
      // quiet rather than narrating a return on almost every folded hand.
      const foldRefund = lockHandAndReturnUncalledBet();
      if (foldRefund && foldRefund.playerId !== winner.id) announceReturn(foldRefund);

      const playerPositions = calculatePlayerPositions(updatedPlayers, gameState.dealerId);

      // Award pots to the winner
      const awardResult = awardPotsToWinners(
        cleanPots,
        [winner.id],
        updatedPlayers,
        playerPositions
      );

      // What the winner is shown. A return to the winner is their own money, so
      // it is added here to keep the headline number equal to the pot they saw.
      const winnerTotal = (awardResult.playerWinnings[winner.id] || 0) +
        (foldRefund && foldRefund.playerId === winner.id ? foldRefund.amount : 0);

      // Update players with winnings and clear hand contributions + street
      // action log. Must zero totalPotContribution / currentRoundBet here —
      // same as GameBoard.handleConfirmWinner — otherwise pot chips stay
      // attributed to contributors after they've already been added to the
      // winner's stack. Mid-game Balances (stack + contribution) would
      // double-count until the next hand reset. Clearing roundActions is
      // the same mechanism a new street uses, so leftover Bet $ lines
      // cannot sit on a $0 pot.
      const awarded = applyAwardedHand(
        updatedPlayers,
        updatedGameState,
        awardResult,
        {
          winnerIds: [winner.id],
          winnerWinnings: { [winner.id]: winnerTotal },
          refunds: awardResult.refunds || {},
          handNumber: gameState.handNumber,
          ts: Date.now()
        },
        { stageProgress: getStageProgress('SHOWDOWN') }
      );
      updatedPlayers.length = 0;
      awarded.players.forEach(player => updatedPlayers.push(player));
      updatedGameState = awarded.gameState;

      // 🔥 ANALYTICS: Track hand completion by fold-out (host only)
      // Triggers when everyone folds and one player wins automatically - increments hands_completed counter
      const isHost = updatedPlayers.some(p => p.id === gameState.hostId && p.isHost);
      trackHandComplete(isHost);
      
      console.log(`🎮 Hand over - ${winner.name} wins by fold and pot awarded: ${winnerTotal}`);
    } else {
      // Check if all active players are all-in (should go directly to showdown)
      const playersWhoCanAct = activePlayers.filter(p => !p.isAllIn);

      if (playersWhoCanAct.length === 0 && activePlayers.length > 1) {
        const refund = lockHandAndReturnUncalledBet();
        if (refund) announceReturn(refund);

        // All active players are all-in - go directly to showdown for manual winner declaration
        updatedGameState = {
          ...updatedGameState,
          stage: 'SHOWDOWN',
          currentTurnPlayerId: null,
          pots: cleanPots,
          canAdvanceStage: false,
          nextStage: null,
          stageProgress: getStageProgress('SHOWDOWN')
        };
        console.log(`🎮 All players all-in - going directly to showdown for manual winner declaration`);
      } else if (gameState.stage === 'RIVER') {
        // Belt and braces: river betting that ends level leaves nothing unmatched,
        // so this is normally a no-op. It runs anyway so no route to showdown can
        // carry an uncalled wager into the pot.
        const refund = lockHandAndReturnUncalledBet();
        if (refund) announceReturn(refund);

        // River betting complete - hand is over, go to showdown
        updatedGameState = {
          ...updatedGameState,
          stage: 'SHOWDOWN',
          currentTurnPlayerId: null,
          pots: cleanPots,
          canAdvanceStage: false,
          nextStage: null,
          stageProgress: getStageProgress('SHOWDOWN')
        };
        console.log(`🎮 River complete - going to showdown`);
      } else {
        // Betting round complete - mark for host advancement
        const nextStage = advanceStage(gameState.stage);
        updatedGameState = {
          ...updatedGameState,
          currentTurnPlayerId: null,
          pots: cleanPots,
          canAdvanceStage: true,
          nextStage: nextStage,
          nextStageName: getNextStageName(gameState.stage),
          stageProgress: getStageProgress(gameState.stage)
        };
        console.log(`🎮 STAGE ADVANCEMENT DETECTED:`, {
          canAdvanceStage: true,
          nextStage: nextStage,
          nextStageName: getNextStageName(gameState.stage),
          currentStage: gameState.stage
        });
      }
    }
  } else {
    // Move to next player
    const nextPlayerId = getNextPlayerToAct(updatedPlayers, playerId);
    updatedGameState = {
      ...updatedGameState,
      currentTurnPlayerId: nextPlayerId,
      pots: cleanPots,
      canAdvanceStage: false,
      nextStage: null,
      stageProgress: getStageProgress(gameState.stage)
    };
    
    console.log(`🎮 Moving to next player: ${nextPlayerId}`);
  }
  
  return {
    updatedPlayers,
    updatedGameState,
    // Same sentence the table sees in the action log, reused verbatim as the
    // undo button's label so "Alex folded" reads identically in both places.
    actionDescription
  };
}

/**
 * Send and immediately process a player action
 * @param {string} roomCode - Room code
 * @param {string} playerId - Player ID
 * @param {Object} action - Action object { type, amount? }
 * @param {Object} gameState - Current game state
 * @param {Array} players - Current players array
 * @param {Object} settings - Game settings
 * @param {string} hostId - Host ID for Firebase updates
 */
export async function sendAndProcessAction(roomCode, playerId, action, gameState, players, settings, hostId) {
  try {
    console.log(`🎮 Player ${playerId} performing action:`, action);
    console.log(`🎮 Current players count: ${players.length}`);
    console.log(`🎮 Current game state:`, { stage: gameState.stage, currentTurnPlayerId: gameState.currentTurnPlayerId });
    console.log(`🎮 Player is host:`, playerId === hostId);
    console.log(`🎮 Host ID:`, hostId);
    
    // Process the action immediately
    const result = processAction(gameState, players, playerId, action, settings);
    
    if (!result) {
      console.log(`🎮 Action processing failed`);
      return null;
    }
    
    const { updatedGameState, actionDescription } = result;
    let livePlayers = {};
    try {
      const liveSnap = await get(ref(database, `rooms/${roomCode}/players`));
      livePlayers = liveSnap.exists() ? liveSnap.val() : {};
    } catch (liveError) {
      console.warn('Could not merge live Away flags:', liveError);
    }
    const updatedPlayers = mergeLiveAwayFlags(
      finalizeSitOutFlags(result.updatedPlayers, result.updatedGameState),
      livePlayers,
      { preserveId: playerId }
    );

    // Update Firebase with new state using host permissions
    const playersObject = {};
    updatedPlayers.forEach(p => {
      playersObject[p.id] = toStoredPlayer(p);
    });

    // Create clean game state object
    const cleanGameState = toStoredGameState(updatedGameState);

    // One-level undo: capture the state as it was BEFORE this action, using the
    // exact same shapes we write, so a restore puts everything back — stacks,
    // bets, fold/all-in flags, turn, street, pots, and (on a fold that ends the
    // hand) the pot award that this very action performed.
    const previousPlayers = {};
    players.forEach(p => {
      previousPlayers[p.id] = toStoredPlayer(p);
    });
    const undoSnapshot = {
      gameState: toStoredGameState(gameState),
      players: previousPlayers,
      description: actionDescription || 'last action',
      handNumber: gameState.handNumber || 1,
      ts: Date.now()
    };

    console.log(`🎮 Updating Firebase with clean data structures:`, {
      canAdvanceStage: cleanGameState.canAdvanceStage,
      nextStage: cleanGameState.nextStage,
      nextStageName: cleanGameState.nextStageName,
      stage: cleanGameState.stage
    });

    try {
      // Single multi-path write: players, game state and the undo snapshot land
      // together or not at all. Previously these were two sequential awaits,
      // which could leave players and gameState disagreeing on failure — and a
      // snapshot that disagreed with the state it describes would be worse than
      // having no undo at all.
      await update(ref(database, `rooms/${roomCode}`), {
        ...playerFieldUpdates(playersObject, {
          awayFlags: 'actor',
          actorId: playerId,
          livePlayers,
        }),
        gameState: cleanGameState,
        undoSnapshot,
        lastActivity: Date.now()
      });
      console.log(`🎮 Action processed successfully: ${action.type}`);
      return result;
    } catch (firebaseError) {
      console.error('🔥 Firebase update error:', firebaseError);
      console.error('🔥 Players object:', JSON.stringify(playersObject, null, 2));
      console.error('🔥 Game state:', JSON.stringify(cleanGameState, null, 2));
      throw firebaseError;
    }
  } catch (error) {
    console.error('Failed to process action:', error);
    throw error;
  }
}



/**
 * Get the next player to act while maintaining proper seating order
 * @param {Array} players - Players array in original seating order
 * @param {string} currentPlayerId - Current player ID
 * @returns {string|null} Next player ID or null if no one can act
 */
function getNextPlayerToAct(players, currentPlayerId) {
  console.log(`🎮 TURN ORDER: Finding next player after ${currentPlayerId}`);
  console.log(`🎮 TURN ORDER: All players:`, players.map(p => `${p.name}(${p.id.slice(-3)}) - folded:${p.hasFolded}, active:${p.isActive}, allIn:${p.isAllIn}, stack:${p.stack}`));
  
  // Get players who can potentially act (haven't folded, are active, not all-in, have chips)
  const playersWhoCanAct = players.filter(p => !p.hasFolded && p.isActive && !p.isAllIn && p.stack > 0);
  
  if (playersWhoCanAct.length === 0) {
    console.log(`🎮 TURN ORDER: No players can act`);
    return null; // No one left to act
  }
  
  // HEADS-UP FIX: If there's only 1 player who can act, check if they need to act
  if (playersWhoCanAct.length === 1) {
    const singlePlayer = playersWhoCanAct[0];
    const currentHighestBet = Math.max(...players.map(p => p.currentRoundBet || 0), 0);
    const needsToAct = !singlePlayer.hasActedThisRound || singlePlayer.currentRoundBet < currentHighestBet;
    
    if (needsToAct) {
      console.log(`🎮 TURN ORDER: Single player ${singlePlayer.name} needs to act`);
      return singlePlayer.id;
    } else {
      console.log(`🎮 TURN ORDER: Single player ${singlePlayer.name} has already acted and matched bet`);
      return null; // They've already acted and matched the bet
    }
  }

  // Find current player's position in the ORIGINAL players array (maintains seating order)
  const currentIndex = players.findIndex(p => p.id === currentPlayerId);
  if (currentIndex === -1) {
    console.log(`🎮 TURN ORDER: Current player not found, defaulting to first active player`);
    // Find first player who can act
    for (const player of players) {
      if (playersWhoCanAct.some(p => p.id === player.id)) {
        const currentHighestBet = Math.max(...players.map(p => p.currentRoundBet || 0), 0);
        const needsToAct = !player.hasActedThisRound || player.currentRoundBet < currentHighestBet;
        if (needsToAct) {
          console.log(`🎮 TURN ORDER: First active player who needs to act: ${player.name}`);
          return player.id;
        }
      }
    }
    return null;
  }
  
  console.log(`🎮 TURN ORDER: Current player ${players[currentIndex].name} is at position ${currentIndex}`);
  
  // Navigate clockwise through the ORIGINAL players array to maintain seating order
  for (let i = 1; i < players.length; i++) {
    const nextIndex = (currentIndex + i) % players.length;
    const nextPlayer = players[nextIndex];
    
    console.log(`🎮 TURN ORDER: Checking position ${nextIndex}: ${nextPlayer.name} - canAct:${playersWhoCanAct.some(p => p.id === nextPlayer.id)}`);
    
    // Check if this player can act (is in our filtered list)
    if (playersWhoCanAct.some(p => p.id === nextPlayer.id)) {
      // Player can act if they haven't acted this round OR if they need to call a raise
      const currentHighestBet = Math.max(...players.map(p => p.currentRoundBet || 0), 0);
      const needsToAct = !nextPlayer.hasActedThisRound || nextPlayer.currentRoundBet < currentHighestBet;
      
      console.log(`🎮 TURN ORDER: ${nextPlayer.name} - hasActed:${nextPlayer.hasActedThisRound}, currentBet:${nextPlayer.currentRoundBet}, highestBet:${currentHighestBet}, needsToAct:${needsToAct}`);
      
      if (needsToAct) {
        console.log(`🎮 TURN ORDER: Next player to act: ${nextPlayer.name} at position ${nextIndex}`);
        return nextPlayer.id;
      }
    }
  }
  
  console.log(`🎮 TURN ORDER: No player needs to act - betting round complete`);
  return null; // No one needs to act
}

/**
 * Advance to next stage (host only)
 * @param {string} roomCode - Room code
 * @param {string} hostId - Host ID for verification
 * @returns {Promise} Firebase update promise
 */
export async function advanceToNextStage(roomCode, hostId) {
  try {
    console.log(`🎮 Host advancing stage for room ${roomCode}`);
    
    // Get current room state
    const roomRef = ref(database, `rooms/${roomCode}`);
    const snapshot = await get(roomRef);
    
    if (!snapshot.exists()) {
      throw new Error('Room not found');
    }
    
    const roomData = snapshot.val();
    await requireHostCaller(roomData, hostId);
    
    const gameState = roomData.gameState;
    const players = Object.values(roomData.players || {}).sort((a, b) => (a.joinedAt || 0) - (b.joinedAt || 0));
    
    if (!gameState.canAdvanceStage) {
      throw new Error('Cannot advance stage at this time');
    }
    
    const nextStage = gameState.nextStage;
    
    // Showdown is not a betting street — do not hand someone a turn, or the
    // host's own seat would see Fold/Call/Raise instead of Declare Winner.
    const goingToShowdown = nextStage === 'SHOWDOWN';
    let firstToAct = null;
    if (!goingToShowdown) {
      if (nextStage === 'PRE_FLOP') {
        firstToAct = getFirstToAct(players);
      } else {
        firstToAct = getFirstToActPostFlop(players, gameState.dealerId);
      }
    }
    
    // Reset only street betting fields. A whole-node replace would restore
    // Away on a seat that tapped I'm back after this snapshot was read.
    const resetPlayers = {};
    Object.values(roomData.players || {}).forEach(player => {
      if (!player?.id) return;
      resetPlayers[player.id] = {
        hasActedThisRound: false,
        currentRoundBet: 0
      };
    });
    
    // Update game state
    const updatedGameState = {
      ...gameState,
      stage: nextStage,
      currentTurnPlayerId: firstToAct,
      canAdvanceStage: false,
      nextStage: null,
      nextStageName: null,
      stageProgress: getStageProgress(nextStage),
      lastRaiseAmount: 0, // Reset for new betting round
      incompleteRaise: false,
      roundActions: [],   // New betting round starts a fresh action log
      winnerInfo: null,   // Never carry a winner modal into a new betting round
      autoAdvancedReason: goingToShowdown ? (gameState.autoAdvancedReason || null) : null
    };
    
    await update(ref(database, `rooms/${roomCode}`), {
      ...playerFieldUpdates(resetPlayers, { awayFlags: 'omit' }),
      gameState: updatedGameState,
      lastActivity: Date.now(),
    });
    
    console.log(`🎮 Advanced to ${nextStage}, first to act: ${firstToAct}`);
    
  } catch (error) {
    console.error('Failed to advance stage:', error);
    throw error;
  }
}

// NOTE: Stale-room cleanup now runs server-side via the scheduled GitHub Action
// (.github/workflows/cleanup-rooms.yml). The old in-app setInterval sweep was
// removed so visitors' browsers no longer re-download all of /rooms every 5
// minutes. The cleanupInactiveRooms() function above is kept for reference/manual
// use but is no longer invoked automatically.